/**
 * Bootstrap gate for the utility feature's JavaScript. One place decides WHEN
 * the utility partner manager initializes and WHEN the interaction-only wizard
 * module loads, so first-time, no-interaction visits ship none of that work on
 * the critical (LCP/TBT) path.
 *
 * Manager (`utility-partner-manager.js`): its `initialize()` parses 46 providers
 * and builds every discount rule. That used to run via a DOMContentLoaded
 * auto-init on every page. First paint is server-rendered (`$app:offers` + cart
 * attributes), so for a visitor with no saved selection and no `?utility=` /
 * `?sje=` link that work produces nothing visible — pure deferrable cost. This
 * gate runs `initialize()` immediately only for the cohorts that need it on load
 * (saved selection or marketing link) and otherwise on the first user
 * interaction. `initialize()` is idempotent, so other callers (wizard
 * connectedCallback, postal-code-checker submit) remain safe.
 *
 * Note: the manager *module* is dynamically imported only when a selection
 * exists (bootstrap, price-handler, discount-manager) or when a lookup /
 * Section-Rendering fallback needs it (postal-code-checker, sale-banner).
 * Pricing-path files import helpers from `utility-shared.js`, not this
 * module. `hrs-reminder.js` on cart is the remaining static importer.
 *
 * Wizard (`utility-selector-wizard.js`, ~16 KB gz): imported when the host
 * `<details>` opens (campaign/`?region=`/`popup_delay` auto-open included)
 * or on first pointer/key/touch engagement. Gated on the custom element's
 * presence. Import failure resets the flag and re-arms engagement so a
 * later gesture can retry.
 *
 * NOT handled here: SSO (`utility-partner-sso.js`). Its module installs a
 * document-level `#cart-form` submit interceptor that blocks checkout until
 * enrollment completes, so it must be in the initial module graph before any
 * checkout click — it is loaded eagerly as a top-level module in
 * `snippets/scripts.liquid`, not deferred through this bootstrap.
 *
 * Design mode (theme editor): everything loads eagerly so merchants can preview.
 */

// Canonical selection gate (localStorage / `?utility=`|`?sje=` / legacy cookie).
// Shared so bootstrap, price-handler, and discount-manager can't drift.
import { hasUtilitySelection } from "@theme/utility-shared";

const ENGAGEMENT_EVENTS = ["pointerdown", "keydown", "touchstart"];

const hasWizard = () =>
  document.querySelector("utility-selector-wizard") != null;

let managerBooted = false;
async function bootManager() {
  if (managerBooted) return;
  managerBooted = true;
  try {
    const { getUtilityPartnerManager } =
      await import("@theme/utility-partner-manager");
    await getUtilityPartnerManager().initialize();
  } catch (error) {
    // Chunk/network failure — reset the flag so a later trigger can retry
    // instead of wedging the feature for the rest of the page view.
    managerBooted = false;
    console.error("[utility-bootstrap] manager load failed", error);
  }
}

let wizardLoaded = false;
function loadWizard() {
  if (wizardLoaded || !hasWizard()) return;
  wizardLoaded = true;
  import("@theme/utility-selector-wizard").catch((error) => {
    wizardLoaded = false;
    console.error("[utility-bootstrap] wizard load failed", error);
    // onEngage is one-shot; re-arm so a later gesture can retry the chunk.
    armEngageOnce();
  });
}

function loadInteractionModules() {
  bootManager();
  loadWizard();
}

function watchWizardHosts() {
  for (const wizard of document.querySelectorAll("utility-selector-wizard")) {
    const details = wizard.closest("details");
    if (!details) continue;
    if (details.open) loadWizard();
    details.addEventListener("toggle", () => {
      if (details.open) loadWizard();
    });
  }
}

let engageArmed = false;
function armEngageOnce() {
  if (engageArmed) return;
  engageArmed = true;
  const onEngage = () => {
    engageArmed = false;
    for (const type of ENGAGEMENT_EVENTS) {
      document.removeEventListener(type, onEngage, true);
    }
    loadInteractionModules();
  };
  for (const type of ENGAGEMENT_EVENTS) {
    document.addEventListener(type, onEngage, { capture: true, passive: true });
  }
}

function init() {
  // Theme editor: load everything eagerly so merchants can preview.
  if (window.Shopify?.designMode) {
    loadInteractionModules();
    return;
  }

  // Returning visitor / marketing link: the manager must reflect on load
  // (parity with the old auto-init). First paint is still SSR, so this only
  // drives the post-load reconcile + UtilityPartnerLoadedEvent.
  if (hasUtilitySelection()) {
    bootManager();
  }

  // Wizard must load when the header dropdown opens without a gesture
  // (checkAutoExpand: ?utility=, ?region=, eligible-region popup_delay).
  watchWizardHosts();
  armEngageOnce();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
